import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Terminal, ArrowRight, Sparkles } from "lucide-react";
import { useAuth } from "@/lib/auth";

const INDUSTRIES = ["Consumer Goods", "Retail", "Technology", "Finance", "Energy", "Healthcare", "Manufacturing", "Logistics", "Telecom"];
const ROLES = [
  { v: "analyst", l: "Data / Analyst" },
  { v: "executive", l: "Executive / C-Suite" },
];

const BACKEND = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/$/, "");

export default function Signup() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const refCode = (sp.get("ref") || "").toUpperCase().trim();
  const [referrer, setReferrer] = useState(null); // {first_name, country, tier} | "invalid" | null

  const [form, setForm] = useState({
    name: "",
    company: "",
    industry: "Consumer Goods",
    role: "analyst",
    email: "",
    password: "",
    email_consent: false,
    referral_code: refCode || null,
  });
  const [loading, setLoading] = useState(false);

  // Validate the referral code as a courtesy — shows the analyst's first name
  // and country if the code is real and approved.
  useEffect(() => {
    if (!refCode) return;
    fetch(`${BACKEND}/api/analysts/by-code/${encodeURIComponent(refCode)}`)
      .then((r) => r.ok ? r.json() : null)
      .then((data) => setReferrer(data || "invalid"))
      .catch(() => setReferrer("invalid"));
  }, [refCode]);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email_consent) {
      toast.error("Please tick the email consent checkbox to continue.");
      return;
    }
    setLoading(true);
    try {
      await register(form);
      toast.success("Workspace provisioned. Welcome to DARA.");
      nav("/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-6" data-testid="signup-page">
      <div className="w-full max-w-lg panel">
        <div className="border-b border-zinc-900 px-6 py-3 flex items-center gap-2">
          <Terminal className="w-4 h-4 text-[#FFB000]" />
          <span className="font-heading font-black tracking-tighter">DARA</span>
          <span className="font-mono text-[10px] text-zinc-500 ml-auto">AUTH://provision</span>
        </div>
        <form onSubmit={submit} className="p-8 space-y-4">

          {/* Phase 25.11 — Referral attribution banner */}
          {refCode && referrer && referrer !== "invalid" && (
            <div
              className="border border-[#FFB000]/40 bg-[#FFB000]/[0.06] p-3 flex items-start gap-2.5"
              data-testid="signup-referrer-banner"
            >
              <Sparkles className="w-4 h-4 text-[#FFB000] flex-shrink-0 mt-0.5" />
              <div className="flex-1 text-xs leading-relaxed">
                <div className="text-white">
                  Referred by <strong>{referrer.first_name}</strong>
                  {referrer.country ? ` (${referrer.country})` : ""}
                  {referrer.tier === "senior" ? " · Senior Expert" : ""}
                </div>
                <div className="text-zinc-500 mt-0.5 font-mono text-[10px]">CODE {refCode}</div>
              </div>
            </div>
          )}
          {refCode && referrer === "invalid" && (
            <div
              className="border border-zinc-800 bg-zinc-950/60 p-3 flex items-start gap-2.5"
              data-testid="signup-referrer-invalid"
            >
              <Sparkles className="w-4 h-4 text-zinc-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 text-xs text-zinc-500 leading-relaxed">
                Referral code <code className="font-mono text-zinc-400">{refCode}</code> not recognized.
                You can continue signing up — no attribution will be recorded.
              </div>
            </div>
          )}
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-3">PROVISION WORKSPACE</div>
            <h1 className="font-heading font-black text-3xl tracking-tighter uppercase">Deploy your<br /> terminal.</h1>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Full name">
              <input data-testid="signup-name" required value={form.name} onChange={update("name")} className={fieldCls} />
            </Field>
            <Field label="Company">
              <input data-testid="signup-company" required value={form.company} onChange={update("company")} className={fieldCls} />
            </Field>
            <Field label="Industry">
              <select data-testid="signup-industry" value={form.industry} onChange={update("industry")} className={fieldCls}>
                {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
              </select>
            </Field>
            <Field label="Role">
              <select data-testid="signup-role" value={form.role} onChange={update("role")} className={fieldCls}>
                {ROLES.map(r => <option key={r.v} value={r.v}>{r.l}</option>)}
              </select>
            </Field>
            <Field label="Email" full>
              <input data-testid="signup-email" type="email" required value={form.email} onChange={update("email")} className={fieldCls} />
            </Field>
            <Field label="Password (min 6)" full>
              <input data-testid="signup-password" type="password" required minLength={6} value={form.password} onChange={update("password")} className={fieldCls} />
            </Field>
          </div>

          {/* Phase 25.8 — Explicit email consent (ESP / SOC 2 / GDPR) */}
          <label
            className="flex items-start gap-3 cursor-pointer border border-zinc-800 bg-zinc-950/40 p-4 hover:border-zinc-700 transition-colors"
            data-testid="signup-consent-row"
          >
            <input
              type="checkbox"
              checked={form.email_consent}
              onChange={(e) => setForm({ ...form, email_consent: e.target.checked })}
              data-testid="signup-consent-checkbox"
              required
              className="mt-1 accent-[#FFB000] w-4 h-4 flex-shrink-0 cursor-pointer"
            />
            <div className="text-xs text-zinc-300 leading-relaxed">
              <strong className="text-white">
                I agree to receive DARA briefings, alerts, and product emails.
              </strong>{" "}
              <span className="text-zinc-500">
                One-click unsubscribe in any email. See our{" "}
                <Link to="/consent" target="_blank" className="text-[#FFB000] hover:underline">
                  email policy
                </Link>{" "}
                and{" "}
                <Link to="/security" target="_blank" className="text-[#FFB000] hover:underline">
                  Trust Center
                </Link>.
              </span>
            </div>
          </label>

          <button
            type="submit"
            disabled={loading || !form.email_consent}
            data-testid="signup-submit-btn"
            className="w-full bg-[#FFB000] text-black font-bold font-mono text-xs uppercase tracking-wider py-3 hover:bg-[#E59E00] transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "PROVISIONING…" : <>Launch terminal <ArrowRight className="w-4 h-4" /></>}
          </button>

          <div className="text-center">
            <Link to="/login" data-testid="signup-login-link" className="font-mono text-[11px] text-zinc-500 hover:text-white">
              Already deployed? <span className="text-[#FFB000]">Login →</span>
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}

const fieldCls = "mt-2 w-full bg-black border border-zinc-800 px-3 py-2.5 font-mono text-sm text-white focus:outline-none focus:border-[#FFB000]";

function Field({ label, children, full }) {
  return (
    <label className={full ? "col-span-2 block" : "block"}>
      <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</span>
      {children}
    </label>
  );
}
